import { useMemo, useRef } from 'react';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import {
    useIslandFallback,
    type IslandMode,
} from '~/ui-rocco/pages/detail-shared';
import { useProfessionalPrimaryCta } from '../ProfessionalPageCtasProvider';

/**
 * Null-rendering owner of the professional island's persistent pill — the
 * page's primary CTA, always one tap away across every tab (the island is
 * mounted in the persistent layout). Shown whenever no scrolled section claims
 * the island. It reads and opens exactly what the header's primary does.
 *
 * Mobile only: desktop keeps its in-page CTA clusters and no persistent pill.
 * Self-hides when the page offers neither an enquiry nor a meeting.
 */
export const ProfessionalIslandFallback = () => {
    const { mobile } = useAppDisplayModeContext();
    // One surface name for the enquiry and the booking this button opens.
    const primary = useProfessionalPrimaryCta({
        source: 'ProfessionalIsland',
    });

    // Route the action through a ref so the memoised mode's identity only tracks
    // meaningful state — an unstable mode would loop setFallback → re-render.
    const actionRef = useRef<() => void>(() => {});
    actionRef.current = primary.open;

    const mode = useMemo<IslandMode | null>(
        () =>
            mobile && primary.visible
                ? {
                      kind: 'cta',
                      label: primary.label,
                      onClick: () => actionRef.current(),
                  }
                : null,
        [mobile, primary.visible, primary.label],
    );
    useIslandFallback(mode);

    return null;
};
